import React, { useState, useEffect } from "react";
import { graphql } from "gatsby";
import Link from "gatsby-link";
import { H2, P, Select, A, H6, H3 } from "@upsolve/ui";
import { EVENTS, STATES, EXCLUDED_STATES } from "@upsolve/shared";
import { track } from "../components/analytics/track";
import * as CONTENT_GROUPS from "../components/analytics/contentGroups";
import MainFooter from "../components/Layout/Footer";
import Header from "../components/Layout/Header";
import SEO from "../components/SEO/SEO";
import TopLevelLayout from "../components/Layout/TopLevelLayout";
import ArticleTile from "../components/Learn/ArticleTile.div";
import LearnArticleTile from "../components/Learn/LearnArticleTile";
import { CTATarget } from "../components/CTA/getCTAData";
import { LearnArticleTagPageStyled } from "../templates/LearnArticleTag";
import { getSortedTagsWithBankruptcyState, getTagUrl } from "../components/Learn/tagUtils";
import { kebabCase, get } from "lodash";

type TStateKey = keyof typeof STATES;

const contentGroup = CONTENT_GROUPS.LEARN_ARTICLE;
const templateProps = {
  pageComponentName: "BankruptcyByStatePage",
  pageComponentVersion: "1",
};
const trackingPageContext = {
  ...templateProps,
  contentGroup,
  contentLocalized: false,
};

interface IStateOption {
  value: string;
  label: string;
}

interface IArticleNode {
  slug: string;
  title: string;
  author?: {
    name: string;
    slug: string;
  };
  authors?: {
    name: string;
    slug: string;
  }[];
  tags: {
    value: string;
    label: string;
    type: string;
    hasDirectoryPage: boolean;
  }[];
  articleExcerpt?: {
    articleExcerpt: string;
  };
  url?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface IStatePageNode {
  path: string;
  title: string;
}

interface IPageData {
  allLearnArticles: {
    nodes: IArticleNode[];
  };
  allContentfulRedirect: {
    nodes: {
      from: string;
    }[];
  };
  allContentfulPage: {
    nodes: IStatePageNode[];
  };
  tags: {
    nodes: {
      value: string;
      label: string;
      type: string;
      hasDirectoryPage: boolean;
      childTags?: {
        value: string;
        label: string;
        type: string;
      }[];
    }[];
  };
}

// Wrapper around LearnArticleTile that handles custom URLs
const FlexibleArticleTile: React.FC<{ article: IArticleNode }> = ({ article }) => {
  if (article.url) {
    // Override LearnArticleTile with our own for state pages
    return (
      <ArticleTile key={article.slug}>
        <div>
          <H3>
            <a href={article.url}>{article.title}</a>
          </H3>
          {get(article, "articleExcerpt.articleExcerpt") != null && (
            <P>{get(article, "articleExcerpt.articleExcerpt")}</P>
          )}
          <a href={article.url}>Read More →</a>
        </div>
      </ArticleTile>
    );
  }

  // For regular learn articles, use the standard LearnArticleTile
  return <LearnArticleTile article={article} />;
};

const BankruptcyByStatePage: React.FC<{ data: IPageData }> = ({ data }) => {
  const [stateFilter, setStateFilter] = useState<string>("");
  const allArticles = data.allLearnArticles.nodes || [];
  const redirects = data.allContentfulRedirect.nodes || [];
  const statePages = data.allContentfulPage.nodes || [];

  // Set of redirect paths for faster lookups
  const redirectPaths = new Set(redirects.map((redirect) => redirect.from));

  // Filter out articles that have redirects
  const articlesWithoutRedirects = allArticles.filter((article) => {
    if (!article.slug) return false;

    // Check if this article has a redirect
    const articlePath = `/learn/${article.slug}/`;
    return !redirectPaths.has(articlePath);
  });

  // Top states for the guides section
  const topStates: TStateKey[] = ["CA", "TX", "FL", "OH", "NY"];

  // All states for the filter dropdown, excluding territories
  const stateOptions: IStateOption[] = Object.keys(STATES)
    .filter((st) => !EXCLUDED_STATES.includes(st))
    .sort((a, b) => STATES[a as TStateKey].localeCompare(STATES[b as TStateKey]))
    .map((st) => ({ value: st, label: STATES[st as TStateKey] }));

  // Page tracking
  useEffect(() => {
    track(EVENTS.PAGE_VIEW, { pageContext: trackingPageContext });
  }, []);

  // Filter state pages based on the selected state
  const filteredStatePages = stateFilter
    ? statePages.filter((page) => {
        if (!page.path) return false;

        // Extract state code from path (e.g., "/ca/" -> "ca")
        const pathParts = page.path.split("/").filter((part) => part.length > 0);
        if (pathParts.length === 0) return false;

        const pageStateCode = pathParts[0].toLowerCase();
        return pageStateCode === stateFilter.toLowerCase();
      })
    : statePages;

  // Filter articles based on the selected state
  const filteredArticles = stateFilter
    ? articlesWithoutRedirects.filter((article) => {
        if (!article.title) return false;

        // Get the state name from the code
        const stateName = STATES[stateFilter as TStateKey];

        // Check if title contains state name
        return article.title.toLowerCase().includes(stateName.toLowerCase());
      })
    : articlesWithoutRedirects;

  // Convert state pages to a format compatible with LearnArticleTile
  const statePageArticles: IArticleNode[] = filteredStatePages.map((page) => ({
    slug: page.path.replace(/^\/|\/$/g, ""), // Remove leading/trailing slashes
    title: page.title,
    articleExcerpt: { articleExcerpt: `State guide for ${page.title}` },
    tags: [], // No tags for state pages
    url: page.path, // Add URL for direct linking
  }));

  // Combine both types of content with state pages first
  const combinedContent = [...statePageArticles, ...filteredArticles];

  return (
    <TopLevelLayout persistentCta="Chat with an Expert for Free">
      <div className="index-container">
        <SEO
          title="Bankruptcy Information by State | Upsolve"
          description="Learn how bankruptcy works in your state. From court locations to filing procedures, this section covers everything you need to know about filing for bankruptcy where you live."
          image="https://upsolve.org/images/meta-legalaid.png"
          url="https://upsolve.org/bankruptcy-by-state/"
          jsonLD={[
            {
              "@context": "http://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Learn",
                  item: "https://upsolve.org/learn/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Bankruptcy Information by State",
                  item: "https://upsolve.org/bankruptcy-by-state/",
                },
              ],
            },
          ]}
        />
        <Header trackingPageContext={trackingPageContext} ctaTarget={CTATarget.SCREENER} />
        <main>
          <LearnArticleTagPageStyled>
            <section className="cover">
              <div className="blob"></div>
              <H2 as="h1">Bankruptcy Information by State</H2>
              <P as="h4">
                Learn how bankruptcy works in your state. From court locations to filing procedures, this section covers
                everything you need to know about filing for bankruptcy where you live.
              </P>
            </section>

            <section className="main">
              <div className="content">
                <div className="content__breadcrumbs">
                  <ul>
                    <li>
                      <Link to="/learn/">Learning Center</Link>
                    </li>
                    <li>
                      <b>Bankruptcy Information by State</b>
                    </li>
                  </ul>
                </div>

                <div className="content__description">
                  <P>
                    Bankruptcy rules and state exemptions vary depending on where you live. Use these state guides to
                    learn how to file for bankruptcy in your area. Whether you're just starting your research or ready
                    to take the next step, this page can help you understand the state-specific rules that apply to you.
                  </P>
                  <P>
                    <i>This page is your home base for learning about bankruptcy in your state.</i>
                  </P>
                </div>

                <div className="content__filter">
                  <P>
                    <b>Filter by State</b>
                  </P>
                  <Select
                    value={stateFilter}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStateFilter(e.target.value)}
                  >
                    <option value="">All States</option>
                    {stateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="content__articles">
                  {combinedContent.map((article) => (
                    <FlexibleArticleTile key={article.slug} article={article} />
                  ))}
                </div>
              </div>

              <aside>
                <div className="aside__section">
                  <H6>Top State Guides</H6>
                  <ul>
                    {topStates.map((state) => (
                      <li key={state}>
                        <A href={`/${state.toLowerCase()}/`}>{STATES[state as TStateKey]}</A>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="aside__section">
                  <H6>Related Resources</H6>
                  <ul>
                    {getSortedTagsWithBankruptcyState(data.tags.nodes)
                      .filter((node) => node.hasDirectoryPage)
                      .map((tag) => (
                        <li key={kebabCase(tag.value)}>
                          <Link to={getTagUrl(tag)}>{tag.label}</Link>
                        </li>
                      ))}
                  </ul>
                </div>
              </aside>
            </section>
          </LearnArticleTagPageStyled>
        </main>
        <MainFooter />
      </div>
    </TopLevelLayout>
  );
};

export const query = graphql`
  query BankruptcyByStateQuery {
    # Get all redirects to check against
    allContentfulRedirect {
      nodes {
        from
      }
    }

    # Get state pages
    allContentfulPage(
      filter: { type: { eq: "statePage" } }
      sort: { order: ASC, fields: siteLinking___importanceRank }
    ) {
      nodes {
        path
        title
      }
    }

    allLearnArticles: allContentfulLearnArticle(
      filter: { tags: { elemMatch: { value: { eq: "propertyExemptions" } } }, slug: { regex: "/exemption/" } }
      sort: { fields: [siteLinking___importanceRank, createdAt] }
    ) {
      nodes {
        slug
        title
        author {
          name
          slug
        }
        authors {
          name
          slug
        }
        tags {
          value
          label
          type
          hasDirectoryPage
        }
        articleExcerpt {
          articleExcerpt
        }
      }
    }

    tags: allContentfulLearnArticleTag(sort: { fields: [value] }) {
      nodes {
        value
        label
        type
        hasDirectoryPage
        childTags {
          value
          label
          type
        }
      }
    }
  }
`;

export default BankruptcyByStatePage;
